import React from "react";
import PropTypes from "prop-types";
import {
    Icon,
    Modal,
    Tooltip,
    Switch,
    Select,
    InputNumber,
    Input,
    message,
    notification
} from "antd";
import {
    RELATIONSHIP_WITH_BORROWER_OPTIONS,
    RELATIONSHIP_WITH_BORROWER_UNDEFINED
} from "../data-utils";
import { connect } from "react-redux";
import { initiateOzonetelManualCall } from "services/telephony-service";
import { isEmail } from "components/helper";
import { scoreBand } from "../data-utils";
import { lookupApplicantHit } from "../trackwizz-utils";
import ApplicantAvatar from "./applicant-avatar";

// Renders the Probable-Hit / Confirmed-Hit cell as a symbol.
//   undefined / null → "—"  (no screening record for this applicant)
//   ""               → "✓"  (screening ran, no matches)
//   "…"              → "✗"  (screening ran, matches present)
function renderHitCell(hit) {
    if (hit === undefined || hit === null) {
        return (
            <span className={"cc-kyc-empty"} title={"No screening data"}>—</span>
        );
    }
    const str = String(hit).trim();
    const hasHit = str.length > 0 && str.toLowerCase() !== "no hit";
    return hasHit ? (
        <span className={"cc-kyc-hit cc-kyc-hit--bad"} title={str}>✗</span>
    ) : (
        <span className={"cc-kyc-hit cc-kyc-hit--good"} title={"No hit"}>✓</span>
    );
}

const { Option } = Select;

const DASH = "—";

// Attractive, unmistakably-"error" notification: red filled icon, red accent
// border and a soft red background, used for all call-failure cases.
function notifyCallError(title, description) {
    notification.error({
        message: (
            <span style={{ fontWeight: 700, color: "#cf1322", fontSize: 15 }}>
                {title}
            </span>
        ),
        description: (
            <span style={{ color: "#595959", fontSize: 13 }}>{description}</span>
        ),
        icon: (
            <Icon
                type={"close-circle"}
                theme={"filled"}
                style={{ color: "#ff4d4f" }}
            />
        ),
        style: {
            background: "#fff1f0",
            border: "1px solid #ffccc7",
            borderLeft: "4px solid #ff4d4f",
            borderRadius: 8,
            boxShadow: "0 6px 16px rgba(255, 77, 79, 0.18)"
        },
        duration: 6
    });
}

// Normalises a stored phone value to the last 10 digits Ozonetel expects.
export function toDialNumber(value) {
    if (!value) {
        return null;
    }
    const digits = String(value).replace(/\D/g, "");
    return digits.length >= 10 ? digits.slice(-10) : null;
}

// "33.33%" | "33.33" | 33.33 -> 33.33; anything unparseable -> 0.
function shareholdingNumber(value) {
    if (value === null || value === undefined || value === "") {
        return 0;
    }
    const n = Number(String(value).replace("%", ""));
    return isFinite(n) && !Number.isNaN(n) ? n : 0;
}

// Sum of every OTHER applicant's shareholding. A partner's new stake is capped
// at whatever is left of the 100% after the others are counted, so the firm can
// never be shown as more than wholly owned.
function otherApplicantsShareholding(applicants, associateId) {
    return (applicants || []).reduce((sum, a) => {
        if (String(a.associateId) === String(associateId)) {
            return sum;
        }
        return sum + shareholdingNumber(a.shareholding);
    }, 0);
}

// Masks an Aadhaar number to its last 4 digits: "•••• •••• 1234".
function maskAadhaar(value) {
    if (!value) {
        return null;
    }
    const digits = String(value).replace(/\s/g, "");
    if (digits.length <= 4) {
        return digits;
    }
    return "•••• •••• " + digits.slice(-4);
}

// A document-backed value: plain text, or a link that opens the viewer tab.
const DocValue = ({ value, hasDoc, onOpen, blocked }) => {
    if (!value) {
        return <span className={"cc-kyc-empty"}>{DASH}</span>;
    }
    if (hasDoc) {
        // Blocked = credit appraisal team who lacks permission to view docs.
        // Cursor becomes "not-allowed" on hover so the disallowed action is
        // obvious BEFORE the click; icon swaps to a stop symbol; title
        // explains why. Click still fires onOpen — the section-level guard
        // then shows the toast — so behaviour is consistent.
        return (
            <button
                type={"button"}
                className={
                    "cc-kyc-link" + (blocked ? " cc-kyc-link--blocked" : "")
                }
                onClick={onOpen}
                title={blocked ? "You don't have permission to view this document" : undefined}
                style={blocked ? { cursor: "not-allowed" } : undefined}
            >
                <span className={"cc-kyc-mono"}>{value}</span>
                <Icon
                    type={blocked ? "stop" : "eye"}
                    className={"cc-kyc-link__icon"}
                />
            </button>
        );
    }
    return <span className={"cc-kyc-mono"}>{value}</span>;
};

DocValue.propTypes = {
    value: PropTypes.node,
    hasDoc: PropTypes.bool,
    onOpen: PropTypes.func,
    blocked: PropTypes.bool
};

const TextValue = ({ value, truncate }) =>
    value ? (
        <span
            className={truncate ? "cc-kyc-text cc-kyc-text--truncate" : "cc-kyc-text"}
            title={truncate ? String(value) : undefined}
        >
            {value}
        </span>
    ) : (
        <span className={"cc-kyc-empty"}>{DASH}</span>
    );

TextValue.propTypes = { value: PropTypes.node, truncate: PropTypes.bool };

/**
 * Relationship-with-borrower cell: reads as plain text with a pencil, and only
 * becomes a Select once the pencil is clicked. Picking a value saves and exits
 * edit mode; blur / Escape cancels without saving.
 */
class RelationCell extends React.Component {
    state = { editing: false };

    startEdit = () => this.setState({ editing: true });

    stopEdit = () => this.setState({ editing: false });

    handleChange = value => {
        const { applicant, onChange } = this.props;
        this.setState({ editing: false });
        // Cleared => reset to UNDEFINED (the API ignores a null).
        onChange(
            applicant.associateId,
            value === undefined
                ? RELATIONSHIP_WITH_BORROWER_UNDEFINED
                : value
        );
    };

    render() {
        const { applicant, onChange, saving } = this.props;
        const editable = Boolean(onChange && applicant.associateId);

        if (this.state.editing && editable) {
            return (
                <Select
                    size={"small"}
                    autoFocus
                    defaultOpen
                    allowClear
                    className={"cc-kyc-relation"}
                    placeholder={"Select"}
                    defaultValue={
                        applicant.relationshipValue === null
                            ? undefined
                            : applicant.relationshipValue
                    }
                    dropdownMatchSelectWidth={false}
                    onChange={this.handleChange}
                    onBlur={this.stopEdit}
                    onInputKeyDown={e => {
                        if (e.key === "Escape") {
                            this.stopEdit();
                        }
                    }}
                >
                    {RELATIONSHIP_WITH_BORROWER_OPTIONS.map(o => (
                        <Option key={o.value} value={o.value}>
                            {o.label}
                        </Option>
                    ))}
                </Select>
            );
        }

        return (
            <div className={"cc-kyc-relation-cell"}>
                <TextValue value={applicant.relationshipWithBorrower} />
                {editable ? (
                    <Tooltip
                        title={
                            applicant.relationshipWithBorrower
                                ? "Edit relation"
                                : "Set relation"
                        }
                    >
                        <button
                            type={"button"}
                            className={"cc-kyc-edit-btn"}
                            aria-label={"Edit relation with borrower"}
                            disabled={saving}
                            onClick={this.startEdit}
                        >
                            <Icon type={saving ? "loading" : "edit"} />
                        </button>
                    </Tooltip>
                ) : null}
            </div>
        );
    }
}

RelationCell.propTypes = {
    applicant: PropTypes.object.isRequired,
    onChange: PropTypes.func,
    saving: PropTypes.bool
};

/**
 * Shareholding cell. Read-only text by default; for a PARTNERSHIP the stake is
 * split across partners and credit needs to correct it, so a pencil opens an
 * inline % input with an explicit Update button (blur alone must not save — a
 * mis-typed stake is a real underwriting error).
 *
 * `editable` is driven by the company's constitution, not by the row.
 */
class ShareholdingCell extends React.Component {
    state = { editing: false, draft: null };

    startEdit = () => {
        const raw = this.props.applicant.shareholding;
        // "33.33%" -> 33.33 for the numeric input.
        const n = raw === null || raw === undefined
            ? null
            : Number(String(raw).replace("%", ""));
        this.setState({
            editing: true,
            draft: isFinite(n) && !Number.isNaN(n) ? n : null
        });
    };

    cancel = () => this.setState({ editing: false, draft: null });

    submit = () => {
        const { applicant, onUpdate, othersTotal } = this.props;
        const { draft } = this.state;
        if (draft === null || draft === undefined || draft === "") {
            message.error("Enter a shareholding %");
            return;
        }
        if (draft < 0 || draft > 100) {
            message.error("Shareholding must be between 0 and 100");
            return;
        }
        // Total across ALL applicants cannot exceed 100% — a firm cannot be more
        // than wholly owned. Rounded to 2dp before comparing so float noise
        // (33.33 × 3 = 99.99000000000001) never trips a valid split.
        const round2 = n => Math.round(n * 100) / 100;
        const nextTotal = round2(othersTotal + draft);
        const currentTotal = round2(
            othersTotal + shareholdingNumber(applicant.shareholding)
        );

        // IMPORTANT: stored data can ALREADY be over 100% (e.g. two applicants
        // each saved at 100%). A flat "next total must be <= 100" rule would then
        // reject every edit and leave the user with no way to repair it. So an
        // edit is allowed when it either lands inside the cap OR strictly reduces
        // the total — you can always walk a broken split back down to a valid one.
        if (nextTotal > 100 && nextTotal >= currentTotal) {
            // Return BEFORE onUpdate so no API call is made.
            message.error(
                `Shareholding total cannot be more than 100% (would be ${nextTotal}%). ` +
                    `Other applicants already hold ${round2(othersTotal)}%.`
            );
            return;
        }
        this.setState({ editing: false });
        onUpdate(applicant.associateId, draft);
    };

    render() {
        const { applicant, editable, saving } = this.props;
        const canEdit = Boolean(editable && applicant.associateId);

        if (this.state.editing && canEdit) {
            const round2 = n => Math.round(n * 100) / 100;
            const others = round2(this.props.othersTotal);
            // Headroom left after the other applicants. This is a HINT ONLY — it
            // is deliberately NOT the input's `max`: when the stored data is
            // already over 100% the headroom is 0, and clamping the field to 0
            // would make the value un-editable (and silently rewrite what the
            // user typed). The real rule is enforced in submit().
            const remaining = round2(Math.max(0, 100 - others));
            const overCap = others > 100;
            return (
                <div className={"cc-kyc-share-edit"}>
                    <InputNumber
                        size={"small"}
                        autoFocus
                        min={0}
                        max={100}
                        step={0.01}
                        precision={2}
                        value={this.state.draft}
                        onChange={v => this.setState({ draft: v })}
                        onPressEnter={this.submit}
                        style={{ width: 76 }}
                    />
                    <button
                        type={"button"}
                        className={"cc-kyc-share-save"}
                        onClick={this.submit}
                    >
                        Update
                    </button>
                    <button
                        type={"button"}
                        className={"cc-kyc-share-cancel"}
                        onClick={this.cancel}
                        aria-label={"Cancel"}
                    >
                        <Icon type={"close"} />
                    </button>
                    <span
                        className={
                            "cc-kyc-share-hint" +
                            (overCap ? " cc-kyc-share-hint--bad" : "")
                        }
                        title={
                            overCap
                                ? "The other applicants already exceed 100% — reduce them"
                                : "Maximum allowed once the other applicants are counted"
                        }
                    >
                        {overCap
                            ? `others ${others}%`
                            : `max ${remaining}%`}
                    </span>
                </div>
            );
        }

        return (
            <div className={"cc-kyc-relation-cell"}>
                <span className={"cc-kyc-mono"}>
                    <TextValue value={applicant.shareholding} />
                </span>
                {canEdit ? (
                    <Tooltip title={"Update shareholding"}>
                        <button
                            type={"button"}
                            className={"cc-kyc-edit-btn"}
                            aria-label={"Update shareholding"}
                            disabled={saving}
                            onClick={this.startEdit}
                        >
                            <Icon type={saving ? "loading" : "edit"} />
                        </button>
                    </Tooltip>
                ) : null}
            </div>
        );
    }
}

ShareholdingCell.propTypes = {
    applicant: PropTypes.object.isRequired,
    onUpdate: PropTypes.func,
    editable: PropTypes.bool,
    saving: PropTypes.bool,
    // Sum of every OTHER applicant's stake — this row is capped at 100 minus it.
    othersTotal: PropTypes.number
};

ShareholdingCell.defaultProps = { othersTotal: 0 };

class EmailCell extends React.Component {
    state = { editing: false, draft: "" };

    startEdit = () => {
        this.setState({
            editing: true,
            draft: this.props.applicant.email || ""
        });
    };

    cancel = () => this.setState({ editing: false, draft: "" });

    submit = () => {
        const { applicant, onUpdate } = this.props;
        const value = this.state.draft.trim();
        if (!value) {
            message.error("Enter an email address");
            return;
        }
        if (!isEmail(value)) {
            message.error("Enter a valid email address");
            return;
        }
        this.setState({ editing: false });
        onUpdate(applicant.associateId, value);
    };

    render() {
        const { applicant, editable, saving } = this.props;
        const canEdit = Boolean(editable && applicant.associateId);

        if (this.state.editing && canEdit) {
            return (
                <div className={"cc-kyc-share-edit"}>
                    <Input
                        size={"small"}
                        autoFocus
                        value={this.state.draft}
                        onChange={e =>
                            this.setState({ draft: e.target.value })
                        }
                        onPressEnter={this.submit}
                        style={{ width: 170 }}
                    />
                    <button
                        type={"button"}
                        className={"cc-kyc-share-save"}
                        onClick={this.submit}
                    >
                        Update
                    </button>
                    <button
                        type={"button"}
                        className={"cc-kyc-share-cancel"}
                        onClick={this.cancel}
                        aria-label={"Cancel"}
                    >
                        <Icon type={"close"} />
                    </button>
                </div>
            );
        }

        return (
            <div className={"cc-kyc-relation-cell"}>
                <TextValue value={applicant.email} truncate />
                {canEdit ? (
                    <Tooltip title={applicant.email ? "Edit email" : "Add email"}>
                        <button
                            type={"button"}
                            className={"cc-kyc-edit-btn"}
                            aria-label={"Edit email"}
                            disabled={saving}
                            onClick={this.startEdit}
                        >
                            <Icon type={saving ? "loading" : "edit"} />
                        </button>
                    </Tooltip>
                ) : null}
            </div>
        );
    }
}

EmailCell.propTypes = {
    applicant: PropTypes.object.isRequired,
    onUpdate: PropTypes.func,
    editable: PropTypes.bool,
    saving: PropTypes.bool
};

/**
 * Applicants KYC matrix showing actual values: PAN / Aadhaar (clickable to the
 * document when on file), photo, mobile, email, address and CIBIL score.
 * "View" opens the full document viewer.
 */
// Confirms intent, then fires the Ozonetel manual-call request via Chitthi.
export function confirmAndCall(applicant, userId, caseId) {
    const number = toDialNumber(applicant.phone);
    if (!number) {
        message.error("No valid 10-digit mobile number available to call.");
        return;
    }
    Modal.confirm({
        title: "Initiate call?",
        content: `Are you sure you want to initiate a call to ${applicant.name ||
            "this applicant"} (${number})?`,
        okText: "Yes, Call",
        cancelText: "Cancel",
        onOk: () => {
            return initiateOzonetelManualCall(number, caseId)
                .then(res => {
                    const data = (res && res.data) || {};
                    const httpOk =
                        res && (res.status === 200 || res.status === 201);
                    // Chitthi returns HTTP 200 even when the call can't be
                    // placed (e.g. "Agent is not available") — the real outcome
                    // is in the body status. Treat anything that isn't an
                    // explicit success as a failure.
                    const succeeded =
                        httpOk &&
                        String(data.status || "").toLowerCase() !== "fail";
                    if (!succeeded) {
                        notifyCallError(
                            "Call could not be initiated",
                            data.message ||
                                "Failed to initiate the call. Please try again."
                        );
                        return;
                    }
                    // Call placed — open the agent console softphone (the
                    // Ozonetel toolbar iframe with the live call + disconnect
                    // controls).
                    if (window.OzonetelBridge) {
                        window.OzonetelBridge.open();
                    }
                    notification.success({
                        message: "Call initiated",
                        description: `Connecting to ${applicant.name ||
                            "the applicant"} (${number}) via Ozonetel.`,
                        duration: 4
                    });
                })
                .catch(err => {
                    notifyCallError(
                        "Call failed",
                        (err.response &&
                            err.response.data &&
                            err.response.data.message) ||
                            "Error initiating call. Please try again."
                    );
                    // Re-throw so antd keeps the dialog's OK button in error state.
                    throw err;
                });
        }
    });
}

const KycSummaryTable = ({
    applicants,
    applicantHits,
    onView,
    userId,
    caseId,
    onToggleMainBorrower,
    savingMainBorrowerId,
    onEditPermanentAddress,
    onEditResidenceAddress,
    onChangeRelationship,
    savingRelationshipId,
    onUpdateShareholding,
    canEditShareholding,
    savingShareholdingId,
    onUpdateEmail,
    savingEmailId,
    docViewBlocked
}) => (
    <div className={"cc-kyc-table-wrap"}>
        <table className={"cc-kyc-table"}>
            <thead>
                <tr>
                    <th>Name</th>
                    <th>Main Borrower</th>
                    <th style={{ width: 90 }} title={"Relationship with Borrower"}>
                        Relation
                    </th>
                    <th>Gender</th>
                    <th>DOB (Age)</th>
                    <th>PAN</th>
                    <th>Aadhaar</th>
                    <th title={"% of the company held by this applicant"}>
                        Shareholding
                    </th>
                    <th>Mobile Number</th>
                    <th>Email</th>
                    <th style={{ minWidth: "300px" }}>Residence Address</th>
                    <th style={{ minWidth: "300px" }}>Permanent Address</th>
                    <th>CIBIL</th>
                    <th title={"Trackwizz probable-match hit"}>Probable Hit</th>
                    <th title={"Trackwizz confirmed-match hit"}>Confirmed Hit</th>
                    <th aria-label={"actions"} />
                </tr>
            </thead>
            <tbody>
                {applicants.map((applicant, index) => {
                    const docs = applicant.docsByTile || {};
                    const score = applicant.bureau && applicant.bureau.cibil_score;
                    const band = scoreBand(score);
                    return (
                        <tr key={applicant.id}>
                            {/* <td className={"cc-kyc-table__name"}> */}
                            <td>
                                <div className={"cc-kyc-table__name-wrap"}>
                                    <ApplicantAvatar
                                        size={36}
                                        name={applicant.name}
                                        filePath={docs.photo && docs.photo.file}
                                    />
                                    <div>
                                        <div className={"cc-kyc-table__name-text"}>
                                            {applicant.name || "Applicant"}
                                        </div>
                                        {applicant.type ? (
                                            <div className={"cc-kyc-table__type"}>
                                                {applicant.type}
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            </td>
                            <td>
                                <Switch
                                    size={"small"}
                                    checkedChildren={"Yes"}
                                    unCheckedChildren={"No"}
                                    checked={Boolean(applicant.isMainBorrower)}
                                    loading={
                                        savingMainBorrowerId !== null &&
                                        savingMainBorrowerId !== undefined &&
                                        String(savingMainBorrowerId) ===
                                            String(applicant.caseAssociateId)
                                    }
                                    disabled={
                                        !applicant.caseAssociateId ||
                                        !onToggleMainBorrower
                                    }
                                    onChange={val =>
                                        onToggleMainBorrower &&
                                        onToggleMainBorrower(
                                            applicant.caseAssociateId,
                                            val
                                        )
                                    }
                                />
                            </td>
                            <td style={{ width: 90 }}>
                                <RelationCell
                                    applicant={applicant}
                                    onChange={onChangeRelationship}
                                    saving={
                                        savingRelationshipId ===
                                        applicant.associateId
                                    }
                                />
                            </td>
                            <td>
                                {applicant.gender == "1"
                                    ? "Male"
                                    : applicant.gender == "2"
                                        ? "Female"
                                        : "-"}
                            </td>
                            <td>
                                {applicant.dobLabel ? (
                                    <span className={"cc-kyc-text"}>
                                        {applicant.dobLabel}
                                        {applicant.age
                                            ? ` (${applicant.age})`
                                            : ""}
                                    </span>
                                ) : (
                                    <span className={"cc-kyc-empty"}>{DASH}</span>
                                )}
                            </td>
                            <td>
                                <DocValue
                                    value={applicant.pan}
                                    hasDoc={Boolean(docs.pan)}
                                    onOpen={() => onView(index, "pan")}
                                    blocked={docViewBlocked}
                                />
                            </td>
                            <td>
                                {/* Aadhaar: masked plain text, no eye-icon
                                    → no doc viewer entry from this cell.
                                    The document is still openable via the
                                    KYC viewer modal if credit needs it. */}
                                {applicant.aadhaar ? (
                                    <span className={"cc-kyc-mono"}>
                                        {maskAadhaar(applicant.aadhaar)}
                                    </span>
                                ) : (
                                    <span className={"cc-kyc-empty"}>{DASH}</span>
                                )}
                            </td>
                            <td>
                                <ShareholdingCell
                                    applicant={applicant}
                                    editable={Boolean(
                                        canEditShareholding &&
                                            onUpdateShareholding
                                    )}
                                    saving={
                                        savingShareholdingId ===
                                        applicant.associateId
                                    }
                                    othersTotal={otherApplicantsShareholding(
                                        applicants,
                                        applicant.associateId
                                    )}
                                    onUpdate={onUpdateShareholding}
                                />
                            </td>
                            <td>
                                <TextValue value={applicant.phone} />
                            </td>
                            <td>
                                <EmailCell
                                    applicant={applicant}
                                    editable={Boolean(onUpdateEmail)}
                                    saving={
                                        savingEmailId === applicant.associateId
                                    }
                                    onUpdate={onUpdateEmail}
                                />
                            </td>
                            <td>
                                <div className={"cc-kyc-permaddr"}>
                                    <TextValue value={applicant.address} />
                                    {onEditResidenceAddress ? (
                                        <Tooltip
                                            title={
                                                applicant.address
                                                    ? "Edit residence address"
                                                    : "Add residence address"
                                            }
                                        >
                                            <button
                                                type={"button"}
                                                className={"cc-kyc-edit-btn"}
                                                aria-label={
                                                    "Edit residence address"
                                                }
                                                onClick={() =>
                                                    onEditResidenceAddress(
                                                        index
                                                    )
                                                }
                                            >
                                                <Icon
                                                    type={
                                                        applicant.address
                                                            ? "edit"
                                                            : "plus-circle"
                                                    }
                                                />
                                            </button>
                                        </Tooltip>
                                    ) : null}
                                </div>
                            </td>
                            <td>
                                <div className={"cc-kyc-permaddr"}>
                                    <TextValue
                                        value={applicant.permanentAddress}
                                    />
                                    {onEditPermanentAddress ? (
                                        <Tooltip
                                            title={
                                                applicant.permanentAddress
                                                    ? "Edit permanent address"
                                                    : "Add permanent address"
                                            }
                                        >
                                            <button
                                                type={"button"}
                                                className={"cc-kyc-edit-btn"}
                                                aria-label={
                                                    "Edit permanent address"
                                                }
                                                onClick={() =>
                                                    onEditPermanentAddress(
                                                        index
                                                    )
                                                }
                                            >
                                                <Icon
                                                    type={
                                                        applicant.permanentAddress
                                                            ? "edit"
                                                            : "plus-circle"
                                                    }
                                                />
                                            </button>
                                        </Tooltip>
                                    ) : null}
                                </div>
                            </td>
                            <td>
                                {score ? (
                                    <span
                                        className={
                                            "cc-kyc-cibil cc-kyc-cibil--" +
                                            band.tone
                                        }
                                    >
                                        {score}
                                    </span>
                                ) : (
                                    <span className={"cc-kyc-empty"}>{DASH}</span>
                                )}
                            </td>
                            {(() => {
                                const hit = lookupApplicantHit(
                                    applicantHits,
                                    applicant.name,
                                    index
                                );
                                return (
                                    <React.Fragment>
                                        <td>{renderHitCell(hit && hit.probable)}</td>
                                        <td>{renderHitCell(hit && hit.confirmed)}</td>
                                    </React.Fragment>
                                );
                            })()}
                            <td className={"cc-kyc-table__action"}>
                                <button
                                    type={"button"}
                                    className={"cc-link-btn"}
                                    onClick={() => onView(index)}
                                >
                                    <Icon type={"eye"} /> View
                                </button>
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    </div>
);

KycSummaryTable.propTypes = {
    applicants: PropTypes.array.isRequired,
    // Trackwizz hit map keyed by normalised applicant name. See trackwizz-utils.
    applicantHits: PropTypes.object,
    onView: PropTypes.func.isRequired,
    userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    caseId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onToggleMainBorrower: PropTypes.func,
    savingMainBorrowerId: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    onEditPermanentAddress: PropTypes.func,
    onEditResidenceAddress: PropTypes.func,
    onUpdateShareholding: PropTypes.func,
    // Only a Partnership's shareholding is editable (see index.isPartnership).
    canEditShareholding: PropTypes.bool,
    savingShareholdingId: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    onChangeRelationship: PropTypes.func,
    savingRelationshipId: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    onUpdateEmail: PropTypes.func,
    savingEmailId: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ])
};

const mapStateToProps = state => ({
    userId: state.user.getIn(["data", "id"])
});

export default connect(mapStateToProps)(KycSummaryTable);
